import { assertReutrnNonNullable, isNonNullable } from "../../utils";
import { atomRequired } from "../atomRequired";
import { BaseStore } from "./BaseStore";
import { type PrimitiveAtom, atom } from "jotai";
import type { SetStateAction } from "react";

export interface ListStoreOptions<T, K extends keyof any> {
  idKey: keyof T | ((record: T) => K);
  list?: T[];
}
export class ListStore<T, K extends keyof any = string> extends BaseStore {
  protected _ids: PrimitiveAtom<K[]>;
  protected _items: PrimitiveAtom<Record<K, T>>;

  ids = atom((get) => get(this._ids));

  items = atom((get) => get(this._items));

  indexMap = atom((get) => {
    const ids = get(this._ids);
    return ids.reduce(
      (acc, id, index) => {
        acc[id] = index;
        return acc;
      },
      {} as Record<K, number>
    );
  });

  readList = atom((get) => {
    const ids = get(this._ids);
    const map = get(this._items);
    return ids.map((id) => map[id]) as T[];
  });

  list = atom(
    (get) => {
      return get(this.readList);
    },
    (get, set, list: SetStateAction<T[]>) => {
      const currentList = get(this.readList);
      const { ids, map } = this.parseList(
        typeof list === "function" ? list(currentList) : list
      );

      set(this._items, map);
      set(this._ids, ids);
    }
  );

  constructor(private readonly options: ListStoreOptions<T, K>) {
    super();

    const { ids, map } = this.parseList(options.list ?? []);

    this._ids = atom(ids);
    this._items = atom(map);
  }

  setList(list: SetStateAction<T[]>) {
    this.set(this.list, list);
  }

  getId(record: T): K {
    if (typeof this.options?.idKey === "function") {
      return (this.options?.idKey as (record: T) => K)(record);
    }

    return record[this.options?.idKey] as K;
  }

  parseList(list: T[]) {
    const ids: K[] = [];
    const map: Record<K, T> = {} as Record<K, T>;

    for (const item of list) {
      const id = this.getId(item);
      ids.push(id);
      map[id] = item;
    }

    return {
      ids,
      map,
    };
  }

  getItem(id?: K, get = this.get): T | undefined {
    if (!id) {
      return undefined;
    }
    return get(this._items)[id];
  }

  getItemByIndex(index: number, get = this.get): T | undefined {
    return this.getItem(get(this._ids)[index], get);
  }

  getRequiredItem = assertReutrnNonNullable(
    this.getItem.bind(this),
    "Record not found"
  );

  getIndex(id: K, get = this.get): number {
    return get(this.indexMap)[id] ?? -1;
  }

  getRequiredIndex = assertReutrnNonNullable(
    this.getIndex.bind(this),
    "Record not found"
  );

  insertItem(newItem: T, index?: number): T {
    const id = this.getId(newItem);
    if (this.getItem(id)) {
      throw new Error("Record already exists");
    }

    this.set(this._items, (prev) => {
      return {
        ...prev,
        [id]: newItem,
      };
    });
    this.set(this._ids, (prev) => {
      return prev.toSpliced(index ?? prev.length, 0, id);
    });

    return newItem;
  }

  pushItem(newItem: T): T {
    return this.insertItem(newItem);
  }

  upsertItem(id: K, update: T | ((oldItem?: T) => T)): T {
    const oldItem = this.getItem(id);

    if (oldItem) {
      return this.updateItem(id, update);
    }

    const newItem =
      typeof update === "function" ? (update as () => T)() : update;
    return this.insertItem(newItem);
  }

  updateItem<R = T>(
    id: K,
    update: T | ((oldItem: T) => T),
    options?: {
      handleNotFound?: (id: K) => R;
    }
  ): T | R {
    const { handleNotFound } = options ?? {};

    const oldItem = this.getItem(id);
    if (!oldItem) {
      if (handleNotFound) {
        return handleNotFound(id) as any;
      }
      throw new Error("Record not found");
    }

    const newItem =
      typeof update === "function"
        ? (update as (oldItem?: T) => T)(oldItem)
        : update;

    const newId = this.getId(newItem);

    if (newId !== id) {
      const index = this.getIndex(id);
      this.set(this._ids, (prev) => {
        return prev.toSpliced(index, 1, newId);
      });
    }

    this.set(this._items, ({ [id]: _, ...rest }) => {
      return {
        ...rest,
        [newId]: newItem,
      } as Record<K, T>;
    });

    return newItem as any;
  }

  updateIfExists(id: K, update: T | ((oldItem: T) => T)): T | undefined {
    return this.updateItem(id, update, {
      handleNotFound: () => undefined,
    });
  }

  removeItem(id: K): T | undefined {
    const item = this.getItem(id);
    this.set(this._ids, (prev) => prev.filter((i) => i !== id));
    this.set(this._items, ({ [id]: _, ...rest }) => {
      return rest as Record<K, T>;
    });

    return item;
  }

  removeItems(ids: K[]): T[] {
    const items = ids.map((id) => this.getItem(id)).filter(isNonNullable);
    this.set(this._ids, (prev) => prev.filter((id) => !ids.includes(id)));
    this.set(this._items, (prev) => {
      let next = prev;
      for (const id of ids) {
        const { [id]: _, ...rest } = next;
        next = rest as Record<K, T>;
      }
      return next;
    });

    return items;
  }

  hasItem(id: K, get = this.get) {
    return Boolean(this.getItem(id, get));
  }

  reorderItems(ids: K[]) {
    this._validateReordering(ids);
    this.set(this._ids, ids);
  }

  sortItems(compareFn: (a: T, b: T) => number) {
    this.set(this._ids, (prev) => {
      return [...prev].sort((idA, idB) =>
        compareFn(this.getItem(idA)!, this.getItem(idB)!)
      );
    });
  }

  createItemAtom(id: K) {
    return atom((get) => this.getItem(id, get));
  }

  createItemByIndexAtom(index: number) {
    return atom((get) => this.getItemByIndex(index, get));
  }

  createRequiredItemAtom(id: K) {
    return atomRequired(this.createItemAtom(id));
  }

  createIndexAtom(id: K) {
    return atom((get) => this.getIndex(id, get));
  }

  crewateRequiredIndexAtom(id: K) {
    return atomRequired(this.createIndexAtom(id));
  }

  private _validateReordering(ids: K[]) {
    const oldIds = this.get(this._ids);
    if (ids.length !== oldIds.length) {
      throw new Error("Invalid reordering");
    }
    const set = new Set(ids);

    for (const id of oldIds) {
      if (!set.has(id)) {
        throw new Error("Invalid reordering");
      }
      set.delete(id);
    }

    if (set.size) {
      throw new Error("Invalid reordering");
    }
  }
}
